import { useState, useEffect, createRef } from 'react';
import { useRouter } from 'src/hooks/useRouter';

import { useSelector } from 'src/hooks/useSelector';
import Downshift from 'downshift';
import { FaCaretRight } from 'react-icons/fa';

import Event from 'src/models/Event';
import Result from 'src/components/organisms/events/SearchResult';
import Input from './Input';
import CategoryFromSlug from 'src/services/categoryFromSlug';
import { useEventSearch } from 'src/hooks/queries/eventSearch';
import { createProxyEnvironment } from '../../../utils/downshift';
import PluginFrame from 'src/utils/PluginFrame';
import { usePluginConfig } from '../../../contexts/pluginContext';

const AutoComplete = (props) => {
  const {
    elementId,
    defaultValue,
    defaultIsOpen,
    transitionOnEnter,
    currentValue,
    setCurrentValue,
    category,
    inModal,
    setSearchDialogActive,
    placeholder,
    widget,
    resultCount,
  } = props;

  const router = useRouter();
  const pluginConfig = usePluginConfig();
  const urlBase = useSelector((state) => state.baseUrl);
  const publisherSettings = useSelector((state) => state.publisherSettings);
  const frameConfig = useSelector((state) => state.frameConfig);
  const pluginFrame = new PluginFrame(frameConfig);

  const [initialValue, setInitialValue] = useState(defaultValue || currentValue || '');
  const [hits, setHits] = useState([]);
  const [hitCount, setHitCount] = useState(undefined);

  let highlightedColour = '#efefef';
  if (defaultIsOpen) {
    highlightedColour = 'white';
  }

  const categoryName = router.query.category
    ? new CategoryFromSlug(pluginConfig.rootCategories, router.query.category).category?.name
    : null;

  // TODO: We should handle errors gracefully.
  const { data } = useEventSearch(
    {
      searchTerm: currentValue,
      hitsPerPage: resultCount,
    },
    router
  );

  useEffect(() => {
    if (data) {
      let hits = data.events.map((event) => new Event(event, publisherSettings));
      if (data.events.length) {
        hits = hits.concat({ hits: true });
      }
      if (categoryName) {
        hits = hits.concat({ noCategory: true });
      }
      setHits(hits);
      setHitCount(data.meta.nbHits);
    }
  }, [data]);

  const forwardToFullSearchResults = (withoutCategory) => {
    const query = Object.assign({}, router.query);

    if (withoutCategory) {
      delete query.category;
      delete query.subcategory;
    }

    delete query.event;
    delete query.eventId;
    delete query.evPage;
    delete query.category;

    if (widget) {
      window.top.location = `${
        publisherSettings.publisher.url
      }?_evDiscoveryPath=/&query=${encodeURIComponent(currentValue)}`;
    } else {
      router.push({
        pathname:
          withoutCategory || router.pathname.startsWith('/event') ? urlBase : router.pathname,
        query: { ...query, query: currentValue },
        shallow: false,
      });
    }

    if (setSearchDialogActive) {
      setSearchDialogActive(false);
    }
  };

  const selectResult = (selection) => {
    let pushed = false;

    if (widget) {
      window.top.location = selection.absoluteHref;
      return;
    }

    if (frameConfig?.options?.disable_details_page) {
      window.open(selection.mainLink);
    }

    const pushRoute = () => {
      if (!pushed) {
        router.push({
          pathname: selection.href,
        });
      }
      pushed = true;
    };

    if (inModal) {
      pluginFrame.getElementById('main-container').classList.add('hidden');
    }

    pushRoute();
  };

  const cancelSearch = () => {
    setCurrentValue('');

    if (widget) {
      return;
    }

    if (inModal) {
      pluginFrame.getElementById(`${elementId}-input`).focus();
    } else {
      const query = Object.assign({}, router.query);
      delete query.query;

      router.push({
        pathname: router.pathname,
        query: query,
      });
    }
  };

  const blur = () => {
    pluginFrame.getElementById(`${elementId}-input`).blur();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    blur();
  };

  const _handleScroll = (ev) => {
    blur();
  };

  const resultsRef = createRef();

  useEffect(() => {
    resultsRef.current.addEventListener('scroll', _handleScroll);
  }, []);

  const results = hitCount === 1 ? 'result' : 'results';

  let dropdownClasses = `modal-full-height-minus-header z-20 rounded-b-2xl mx-2 pr-2 mt-2.5 overflow-y-auto absolute top-8 block @lg:box-border w-full bg-white font-light
                    @md:border-l @md:border-r @md:border-b @md:ml-8 @md:mr-2 @md:w-3/4 @md:shadow-xl`;

  if (widget) {
    dropdownClasses = `z-20 rounded-b absolute top-10.5 block bg-white font-light border rounded w-full @md:w-1/2`;
  }

  /* wrapping form element so iOS gives us a search button for <input type='search'> */
  const ui = (
    <form className="w-full" action="#" onSubmit={onSubmit}>
      <Downshift
        id={elementId}
        defaultIsOpen={defaultIsOpen}
        environment={createProxyEnvironment(pluginFrame.rootNode)}
        onChange={(selection) => {
          if (selection.hits) {
            forwardToFullSearchResults();
          } else if (selection.noCategory) {
            forwardToFullSearchResults(true);
          } else {
            selectResult(selection);
          }
        }}
        itemToString={(event) => event?.title}
      >
        {({
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          isOpen,
          inputValue,
          highlightedIndex,
          selectedItem,
          getRootProps,
          openMenu,
          closeMenu,
          reset,
        }) => (
          <div className="relative w-full">
            <div
              {...getRootProps({}, { suppressRefError: true })}
              className="w-full inline-block fixed flex relative"
            >
              <Input
                inModal={inModal}
                type="search"
                placeholder={placeholder}
                cancelAction={() => {
                  cancelSearch();
                }}
                {...getInputProps({
                  onChange: (event) => {
                    setCurrentValue(event.currentTarget.value);
                    setInitialValue(false);
                  },
                  onFocus: () => {
                    openMenu();
                  },
                  onBlur: () => {
                    if (inModal) {
                      setInitialValue(defaultValue);
                    }
                  },
                  onKeyDown: (event) => {
                    if (event.key === 'Escape') {
                      // Closes menu, revert to the default
                      setInitialValue(defaultValue);
                      blur();
                    } else if (event.key === 'Enter') {
                      if (inModal) {
                        // Executes search based on current search term
                        closeMenu();
                      } else if (highlightedIndex === null) {
                        forwardToFullSearchResults();
                      }
                    }
                  },
                })}
                value={initialValue || currentValue}
                style={defaultIsOpen ? { fontSize: '16px' } : {}}
              />
            </div>
            <div
              {...getMenuProps({ ref: resultsRef })}
              className={isOpen && currentValue.length ? dropdownClasses : 'hidden'}
            >
              <div className="grid grid-cols-1 gap-4">
                <div className="@lg:border-b border-gray-300 @xl:border-0">
                  <h3 className="font-semibold mt-2 ml-3 @lg:mb-2 text-theme-alt">
                    {hitCount?.toLocaleString()} {results} for &apos;
                    {currentValue}&apos;
                    {categoryName && <span> in {categoryName}</span>}
                  </h3>
                  <ul>
                    {isOpen
                      ? hits.map((item, index) => (
                          <li
                            key={item.id}
                            {...getItemProps({
                              key: JSON.stringify(item),
                              index,
                              item,
                              style: {
                                backgroundColor:
                                  highlightedIndex === index ? highlightedColour : 'white',
                                fontWeight: 'normal',
                              },
                              className: '@lg:rounded',
                            })}
                          >
                            {item.hits ? (
                              <span>
                                {hitCount > 1 && (
                                  <div
                                    className="text-theme-alt p-2 font-semibold active:bg-gray-100"
                                    onClick={() => forwardToFullSearchResults()}
                                  >
                                    See all {hitCount?.toLocaleString()} {results}{' '}
                                    {categoryName && <span> in {categoryName}</span>}{' '}
                                    <FaCaretRight className="inline" />
                                  </div>
                                )}
                              </span>
                            ) : item.noCategory ? (
                              <div
                                className="text-theme-alt p-2 font-semibold active:bg-gray-100"
                                onClick={() => forwardToFullSearchResults(true)}
                              >
                                Search all events for &apos;{currentValue}&apos;{' '}
                                <FaCaretRight className="inline" />
                              </div>
                            ) : (
                              <Result event={item} />
                            )}
                          </li>
                        ))
                      : null}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </Downshift>
    </form>
  );

  return ui;
};

export default AutoComplete;
